import { useEffect, useRef, useState } from "react";
import { IAdSearchValue } from "./fileList";
import { DatePicker } from "../datePicker";
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import { PSM } from "../../util/rpwTypes";
import { Form } from "react-bootstrap";
import { lddGlobalSessionInfo, ProvincesShortCode } from "../navigationTypes";
import "./AdvancedSearchBox.css";
import { RPWPropertyType } from "../../util/rpwConst";
import { useAppSelector } from "../../redux/hooks";
import { handleSetData } from "../List/ListFuncs";
import axios from "axios";

interface IAdSearchProps<T> extends FileListProps {
    listName: "FileList";
    setSearchQuery: (value: string) => void;
    showSearchBox: (value: boolean) => void;
    setData: (value: T[]) => void;
    setDataPage: (value: number) => void;
    setHasMoreFiles: (value: boolean) => void;
    setShowADSearch: (value: boolean) => void;
    setFreesearchData: (value: T[]) => void;
    adSearch: IAdSearchValue;
    setADSearch: (value: IAdSearchValue) => void;
}

interface FileListProps {
    handleLoadData?: (firmId: number, prov: ProvincesShortCode, sid: string, filePage: number, searchText: string, adSearch?: IAdSearchValue, setADSearch?: (val: IAdSearchValue) => void) => Promise<Response>[];
}

const AdvancedSearchBox = <T,>(props: IAdSearchProps<T>) => {
    const SESSION_INFO: lddGlobalSessionInfo = useAppSelector((g) => g.SessionInfo);
    const sessionID: string = useAppSelector((state) => state.SessionID);
    const userID = useAppSelector((state) => state.SessionInfo.firm.id);
    const prov = useAppSelector((state) => state.SessionInfo.appProvinceShort);
    const [PSM, setPSM] = useState<PSM[]>(["P", "S", "M"]);
    const [Lawyer, setLawyer] = useState<string>(props.adSearch.Lawyer ? props.adSearch.Lawyer : "");
    const [LSUCID, setLSUCID] = useState<string>(props.adSearch.LSUCID ? props.adSearch.LSUCID : "");
    const [StaffName, setStaffName] = useState<string>(props.adSearch.StaffName ? props.adSearch.StaffName : "");
    const [ClosingDateAfter, setClosingDateAfter] = useState<string>(props.adSearch.ClosingDateAfter ? props.adSearch.ClosingDateAfter : "");
    const [ClosingDateBefore, setClosingDateBefore] = useState<string>(props.adSearch.ClosingDateBefore ? props.adSearch.ClosingDateBefore : "");
    const [ClientName, setClientName] = useState<string>(props.adSearch.ClientName ? props.adSearch.ClientName : "");
    const [PropertyAddress, setPropertyAddress] = useState<string>(props.adSearch.PropertyAddress ? props.adSearch.PropertyAddress : "");
    const [PropertyType, setPropertyType] = useState<string>(props.adSearch.PropertyType ? props.adSearch.PropertyType : "");
    const [ReLineInclude, setReLineInclude] = useState<string>(props.adSearch.ReLineInclude ? props.adSearch.ReLineInclude : "");
    const [ReLineExclude, setReLineExclude] = useState<string>(props.adSearch.ReLineExclude ? props.adSearch.ReLineExclude : "");
    const [SolicitorName, setSolicitorName] = useState<string>(props.adSearch.SolicitorName ? props.adSearch.SolicitorName : "");
    const [MatterNumber, setMatterNumber] = useState<string>(props.adSearch.MatterNumber ? props.adSearch.MatterNumber : "");
    const [OtherSideMatterNumber, setOtherSideMatterNumber] = useState<string>(props.adSearch.OtherSideMatterNumber ? props.adSearch.OtherSideMatterNumber : "");
    const [Category, setCategory] = useState<("Active" | "Archive" | "All" | "")>(props.adSearch.Category ? props.adSearch.Category : "All");
    const adSearchRef = useRef(null);
    const [combinedUsersList, setCombinedUsersList] = useState<any[]>([]);

    useEffect(() => {
        // allow PSM has lowercase input
        if (props.adSearch.PSM) {
            let psmCheck: PSM[] = [];
            if (typeof props.adSearch.PSM === "object") {
                props.adSearch.PSM.forEach(val => {
                    const temp = val.toLocaleUpperCase();
                    if (temp === "P" || temp === "S" || temp === "M") {
                        psmCheck.push(temp)
                    }
                })
            }
            setPSM(psmCheck);
        }

        // Fetch combined users and staff list
        const fetchCombinedUsers = async () => {
            try {
                // Fetch new users
                const usersResponse = await axios.get(`/${process.env.REACT_APP_UVAPI_ENDPOINT}/api/${userID}/uv/get/users?SessionID=${sessionID}`);
                const formattedNewUsers = usersResponse.data.result.map((user: any) => ({
                    Name: user.Name,
                    FirstName: user.FirstName,
                    LastName: user.LastName,
                    Position: user.JobTitle,
                    UserID: user.LDDUserID,
                    SubUserId: user.SubUserID,
                    LDDUserName: user.SubUsername,
                    type: 'user'
                }));

                // Fetch old users
                const oldUsersResponse = await axios.get(`/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${prov}/${userID}/oldUsers?SessionID=${sessionID}`);
                const formattedOldUsers = oldUsersResponse.data.result.map((user: any) => ({
                    Name: user.Name,
                    UserID: user.UserID,
                    qpickrepository_id: user.qpickrepository_id,
                    FirstName: user.Name.split(' ')[0],
                    LastName: user.Name.split(' ').slice(1).join(' '),
                    LDDUserName: user.LDDUserName,
                    Position: user.Position,
                    type: 'oldUser'
                }));

                // Combine both lists and remove duplicates based on LDDUserName
                const combined = [...formattedNewUsers, ...formattedOldUsers];
                const uniqueCombined = combined.filter((user, index, self) => 
                    index === self.findIndex(u => u.LDDUserName === user.LDDUserName && u.Name === user.Name)
                );

                setCombinedUsersList(uniqueCombined);
            } catch (error) {
                console.error("Error fetching combined users list:", error);
            }
        };

        fetchCombinedUsers();

        // close this component when lose focus
        function handleClickOutside(e) {
            const target = e.target as HTMLElement;
            
            // Check if click is inside react-datepicker popup
            const isDatePickerClick = target.closest('.react-datepicker__popper') || 
                                     target.closest('.react-datepicker') ||
                                     target.closest('[class*="react-datepicker"]');
            
            if (adSearchRef.current && !adSearchRef.current.contains(e.target)
                && e.target.id !== "searchOptionBtn" && e.target.id !== "searchOptionBtn2"
                && !isDatePickerClick) {
                props.setShowADSearch(false);
            }
        }
        // Bind the event listener
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [props, sessionID, userID, prov]);

    function handleFileTypeChange(checked: boolean, val: PSM) {
        setPSM(checked ? [...PSM, val] : [...PSM.filter(v => v !== val)])
    }

    function handleFileStatusChange(checked: boolean, val: "Active" | "Archive") {
        if (checked) {
            if (Category === "") {
                setCategory(val);
            } else {
                setCategory("All");
            }
        } else {
            if (Category === "All") {
                setCategory(val === "Active" ? "Archive" : "Active");
            } else {
                setCategory("");
            }
        }
    }

    return (
        <div ref={adSearchRef} className="overlay" id="advancedSearchBox">
            <Container id="adSearchContainer">
                <Row>
                    <Col className="col-12">
                        <h3>File Details</h3>
                    </Col>
                </Row>

                <Row className="mb-4">
                    <Col className="col-6">
                        <Form.Label>File No.</Form.Label>
                        <Form.Control placeholder="enter file no.." onChange={(e) => setMatterNumber(e.target.value)} value={MatterNumber}></Form.Control>
                    </Col>
                    <Col className="col-3">
                        <Form.Label>Type</Form.Label>
                        <Form.Check
                            inline
                            type="checkbox"
                            id="PFile"
                            className="form-tag"
                            label="Purchase"
                            onChange={(e) => handleFileTypeChange(e.target.checked, "P")}
                            checked={PSM.indexOf("P") !== -1}
                        />
                        <Form.Check
                            inline
                            type="checkbox"
                            id="SFile"
                            className="form-tag"
                            label="Sale"
                            onChange={(e) => handleFileTypeChange(e.target.checked, "S")}
                            checked={PSM.indexOf("S") !== -1}
                        />
                        <Form.Check
                            inline
                            type="checkbox"
                            id="MFile"
                            className="form-tag"
                            label="Mortgage"
                            onChange={(e) => handleFileTypeChange(e.target.checked, "M")}
                            checked={PSM.indexOf("M") !== -1}
                        />
                    </Col>
                    <Col className="col-3">
                        <Form.Label>Status</Form.Label>
                        <Form.Check
                            inline
                            type="checkbox"
                            id="ActiveFile"
                            className="form-tag"
                            label="Active"
                            onChange={(e) => handleFileStatusChange(e.target.checked, "Active")}
                            checked={Category.indexOf("Active") !== -1 || Category.indexOf("All") !== -1}
                        />
                        <Form.Check
                            inline
                            type="checkbox"
                            id="ArchiveFile"
                            className="form-tag"
                            label="Archive"
                            onChange={(e) => handleFileStatusChange(e.target.checked, "Archive")}
                            checked={Category.indexOf("Archive") !== -1 || Category.indexOf("All") !== -1}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col className="col-12">
                        <h3>Basic Information</h3>
                    </Col>
                </Row>

                <Row className="mb-4">
                    <Col className="col-3">
                        <Form.Label>Responsible Lawyer</Form.Label>
                        <Form.Select onChange={(e) => { setLawyer(e.target.value); setLSUCID(e.target.value ? e.target.selectedOptions[0].innerText : ''); }} value={Lawyer}>
                            <option value="">- Select -</option>
                            {SESSION_INFO.firm.Lawyers.map(lawyer =>
                                <option key={lawyer.LSUCID} value={`${lawyer.FirstName} ${lawyer.LastName}`} label={`${lawyer.FirstName} ${lawyer.LastName}`}>{lawyer.LSUCID}</option>
                            )}
                        </Form.Select>
                    </Col>
                    <Col className="col-3">
                        <Form.Label>Firm Contact</Form.Label>
                        <Form.Select onChange={(e) => setStaffName(e.target.value)} value={StaffName}>
                            <option value="">- Select -</option>
                            {combinedUsersList.map((user, index) =>
                                <option 
                                    key={user.type === 'user' ? `user-${user.SubUserId || user.UserID}` : `staff-${user.qpickrepository_id || user.UserID || index}`} 
                                    value={user.Name || `${user.FirstName} ${user.LastName}`}
                                >
                                    {user.Name || `${user.FirstName} ${user.LastName}`}
                                </option>
                            )}
                        </Form.Select>
                    </Col>
                    <Col className="col-6">
                        <Form.Label>Client Name</Form.Label>
                        <Form.Control placeholder="enter client name.." onChange={(e) => setClientName(e.target.value)} value={ClientName}></Form.Control>
                    </Col>
                </Row>

                <Row>
                    <Col className="col-6">
                        <h3>Reline</h3>
                    </Col>
                    <Col className="col-6">
                        <h3>Closing Date</h3>
                    </Col>
                </Row>

                <Row className="mb-4">
                    <Col className="col-3">
                        <Form.Label>Includes</Form.Label>
                        <Form.Control placeholder="enter keywords.." onChange={(e) => setReLineInclude(e.target.value)} value={ReLineInclude}></Form.Control>
                    </Col>
                    <Col className="col-3">
                        <Form.Label>Doesn't Include</Form.Label>
                        <Form.Control placeholder="enter keywords.." onChange={(e) => setReLineExclude(e.target.value)} value={ReLineExclude}></Form.Control>
                    </Col>
                    <Col className="col-3">
                        <Form.Label>After</Form.Label>
                        <DatePicker date={ClosingDateAfter} setDate={setClosingDateAfter} />
                    </Col>
                    <Col className="col-3">
                        <Form.Label>Before</Form.Label>
                        <DatePicker date={ClosingDateBefore} setDate={setClosingDateBefore} />
                    </Col>
                </Row>

                <Row>
                    <Col className="col-12">
                        <h3>Property Information</h3>
                    </Col>
                </Row>

                <Row className="mb-4">
                    <Col className="col-6">
                        <Form.Label>Address</Form.Label>
                        <Form.Control placeholder="enter property address.." onChange={(e) => setPropertyAddress(e.target.value)} value={PropertyAddress}></Form.Control>
                    </Col>
                    <Col className="col-3">
                        <Form.Label>Property Type</Form.Label>
                        <Form.Select onChange={(e) => setPropertyType(e.target.value)} value={PropertyType}>
                            <option value="">- Select -</option>
                            {Object.keys(RPWPropertyType[SESSION_INFO.appProvinceShort]).map(k =>
                                <option key={k} value={k}>{RPWPropertyType[SESSION_INFO.appProvinceShort][k]}</option>
                            )}
                        </Form.Select>
                    </Col>
                </Row>

                <Row>
                    <Col className="col-12">
                        <h3>Other Side Information</h3>
                    </Col>
                </Row>

                <Row className="mb-4">
                    <Col className="col-6">
                        <Form.Label>Other Side Lawyer Name</Form.Label>
                        <Form.Control placeholder="enter other side lawyer name.." onChange={(e) => setSolicitorName(e.target.value)} value={SolicitorName}></Form.Control>
                    </Col>
                    <Col className="col-6">
                        <Form.Label>Other Side File No.</Form.Label>
                        <Form.Control placeholder="enter other side file no.." onChange={(e) => setOtherSideMatterNumber(e.target.value)} value={OtherSideMatterNumber}></Form.Control>
                    </Col>
                </Row>

                <Row>
                    <Col className="col-12 align-right">
                        <Button variant="link" onClick={() => { props.showSearchBox(false); }}>Cancel</Button>
                        <Button variant="primary" disabled={PSM.length === 0 || Category === ""} onClick={() => {
                            let query = ``, adSearch: IAdSearchValue = {};
                            if (PSM.length > 0 && PSM.length < 3) { query += ` type:(${PSM.join()}) `; adSearch.PSM = PSM; }
                            if (Lawyer) { query += ` lawyer:(${Lawyer}) `; adSearch.Lawyer = Lawyer; }
                            if (LSUCID) { query += ` LSUCID:(${LSUCID}) `; adSearch.LSUCID = LSUCID; }
                            if (StaffName) { query += ` staff:(${StaffName}) `; adSearch.StaffName = StaffName; }
                            if (ClosingDateAfter) { query += ` from:(${ClosingDateAfter}) `; adSearch.ClosingDateAfter = ClosingDateAfter; }
                            if (ClosingDateBefore) { query += ` to:(${ClosingDateBefore}) `; adSearch.ClosingDateBefore = ClosingDateBefore; }
                            if (ClientName) { query += ` client:(${ClientName}) `; adSearch.ClientName = ClientName; }
                            if (PropertyAddress) { query += ` address:(${PropertyAddress}) `; adSearch.PropertyAddress = PropertyAddress; }
                            if (PropertyType) { query += ` property:(${PropertyType}) `; adSearch.PropertyType = PropertyType; }
                            if (ReLineInclude) { query += ` reline:(${ReLineInclude}) `; adSearch.ReLineInclude = ReLineInclude; }
                            if (ReLineExclude) { query += ` -reline:(${ReLineExclude}) `; adSearch.ReLineExclude = ReLineExclude; }
                            if (SolicitorName) { query += ` olawyer:(${SolicitorName}) `; adSearch.SolicitorName = SolicitorName; }
                            if (MatterNumber) { query += ` matter:(${MatterNumber}) `; adSearch.MatterNumber = MatterNumber; }
                            if (OtherSideMatterNumber) { query += ` omatter:(${OtherSideMatterNumber}) `; adSearch.OtherSideMatterNumber = OtherSideMatterNumber; }
                            if (Category != "") { query += ` status:(${Category}) `; adSearch.Category = Category; }
                            props.setSearchQuery(query);
                            props.showSearchBox(false);
                            props.setDataPage(2);
                            props.setADSearch(adSearch);
                            const responsePromises = props.handleLoadData(SESSION_INFO.firm.id, SESSION_INFO.appProvinceShort, SESSION_INFO.sid, 1, "", adSearch, props.setADSearch);
                            handleSetData(props.listName, responsePromises, props.setData, props.setHasMoreFiles, props.setFreesearchData);
                        }}>Search</Button>
                    </Col>
                </Row>
            </Container>
        </div>
    )
}

export default AdvancedSearchBox;